import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLocations } from "@/context/LocationContext";
import { toast } from "sonner";
import { PackagePlus, Printer, Pencil, Archive, RotateCcw, Search } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import SupervisorPinModal from "@/components/SupervisorPinModal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHeader, Panel, AllergenTags, StaffInitials } from "@/components/ui-bits";
import BarcodeScanner from "@/components/BarcodeScanner";
import { usePrinter } from "@/context/PrinterContext";
import Field from "@/components/intake/Field";
import IntakeLabelDialog from "@/components/intake/IntakeLabelDialog";
import DateInput from "@/components/DateInput";

const PACK_TYPES = ["bag", "sack", "tub", "bottle", "drum", "jar", "carton", "tray", "unit"];

const empty = {
  supplier_id: "",
  material_id: "",
  supplier_batch: "",
  pack_count: "",
  pack_type: "bag",
  pack_size: "",
  pack_unit: "kg",
  expiry_date: "",
  location_id: "",
  notes: "",
};

export default function Intake() {
  const { params, selected } = useLocations();
  const printer = usePrinter();
  const btReady = printer?.isConnected;
  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);
  const [labelFor, setLabelFor] = useState(null);
  const [picked, setPicked] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [q, setQ] = useState("");
  const [restoreFor, setRestoreFor] = useState(null);
  const needle = q.trim().toLowerCase();
  const visibleRows = showArchived && needle
    ? rows.filter((r) =>
        [r.internal_batch_code, r.supplier_batch, r.material_name, r.supplier_name]
          .some((v) => (v || "").toLowerCase().includes(needle)))
    : rows;
  const restore = async (_who, _note, pin) => {
    try {
      await api.post("/intakes/bulk-restore", { ids: [restoreFor.id], pin });
      toast.success(`${restoreFor.internal_batch_code} restored with ${Number(restoreFor.remaining_before_archive || 0).toFixed(2)} ${restoreFor.unit} stock`);
      setRestoreFor(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Restore failed");
    }
  };
  const allPicked = rows.length > 0 && picked.length === rows.length;
  const togglePick = (id) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const load = async () => {
    const [r, s, m, l] = await Promise.all([
      api.get("/intakes", { params: params(showArchived ? { archived: true } : {}) }),
      api.get("/suppliers"),
      api.get("/materials"),
      api.get("/locations"),
    ]);
    setRows(r.data);
    setPicked([]);
    setSuppliers(s.data);
    setMaterials(m.data);
    setLocations(l.data);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, showArchived]);

  const submit = async (e) => {
    e?.preventDefault?.();
    try {
      const pc = Number(form.pack_count);
      const ps = Number(form.pack_size);
      const total = pc * ps;
      if (!pc || !ps || total <= 0) {
        toast.error("Enter pack count and size per pack");
        return;
      }
      const body = {
        supplier_id: form.supplier_id,
        material_id: form.material_id,
        supplier_batch: form.supplier_batch,
        pack_count: pc,
        pack_type: form.pack_type,
        pack_size: ps,
        pack_unit: form.pack_unit,
        quantity: total,
        unit: form.pack_unit,
        expiry_date: form.expiry_date,
        location_id: form.location_id || null,
        notes: form.notes,
      };
      await (editId
        ? api.patch(`/intakes/${editId}`, body)
        : api.post("/intakes", body));
      toast.success(
        editId
          ? "Intake updated"
          : `Recorded ${pc} ${form.pack_type}${pc !== 1 ? "s" : ""} · ${total} ${form.pack_unit}`
      );
      setOpen(false);
      setEditId(null);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const archive = async (ids) => {
    const n = ids.length;
    setRows((rs) => rs.filter((r) => !ids.includes(r.id)));
    setPicked([]);
    try {
      await api.post("/intakes/bulk-archive", { ids });
      toast.success(`Moved ${n} intake${n !== 1 ? "s" : ""} to archive`, {
        action: {
          label: "Undo",
          onClick: async () => {
            await api.post("/intakes/undo-archive", { ids });
            toast.success("Archive undone");
            load();
          },
        },
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Archive failed");
      load();
    }
  };
  const remove = (id) => archive([id]);

  const startNew = () => {
    setEditId(null);
    setForm(empty);
    setOpen(true);
  };

  const startEdit = (r) => {
    setEditId(r.id);
    setForm({
      supplier_id: r.supplier_id || "",
      material_id: r.material_id || "",
      supplier_batch: r.supplier_batch || "",
      pack_count: r.pack_count ?? "",
      pack_type: r.pack_type || "bag",
      pack_size: r.pack_size ?? "",
      pack_unit: r.pack_unit || "kg",
      expiry_date: r.expiry_date?.slice(0, 10) || "",
      location_id: r.location_id || "",
      notes: r.notes || "",
    });
    setOpen(true);
  };

  return (
    <div>
      <PageHeader title="Goods Intake" eyebrow="Inbound · Step 1">
        <Button
          className="h-12 px-5 gap-2"
          onClick={startNew}
          data-testid="new-intake-button"
        >
          <PackagePlus className="h-5 w-5" /> Record intake
        </Button>
      </PageHeader>

      <Panel
        title={showArchived ? `Archived (${rows.length})` : `Intake records (${rows.length})`}
        action={
          <div className="flex flex-wrap justify-end items-center gap-2">
            {!showArchived && picked.length > 0 && (
              <Button className="h-10 gap-1.5 bg-amber-600 hover:bg-amber-700" onClick={() => archive(picked)} data-testid="intake-bulk-delete">
                <Archive className="h-4 w-4" /> Move to archive ({picked.length})
              </Button>
            )}
            <Button variant="outline" className="h-10 gap-1.5" onClick={() => { setShowArchived((v) => !v); setQ(""); }} data-testid="intake-toggle-archived">
              <Archive className="h-4 w-4" /> {showArchived ? "Back" : "Archive"}
            </Button>
          </div>
        }
      >
        {showArchived && (
          <div className="relative mb-4 max-w-md">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search batch code, supplier batch or material"
              className="h-11 pl-9" data-testid="archive-search-input" />
          </div>
        )}
        {visibleRows.length === 0 ? (
          <div className="text-sm text-stone-500 py-10 text-center" data-testid="intake-empty">
            {showArchived ? (needle ? "No archived intakes match your search." : "The archive is empty.") : <>No intake records yet. Click <strong>Record intake</strong> to begin.</>}
          </div>
        ) : (
          <>
            {/* ---- Table view (landscape tablets + desktop, md+ breakpoint) ---- */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full" data-testid="intake-table">
                <thead className="text-left text-xs uppercase tracking-wider text-stone-500">
                  <tr>
                    {!showArchived && (
                      <th className="w-10">
                        <Checkbox
                          checked={allPicked}
                          onCheckedChange={(v) => setPicked(v ? rows.map((r) => r.id) : [])}
                          data-testid="intake-select-all"
                          aria-label="Select all"
                        />
                      </th>
                    )}
                    <th className="py-3">Internal code</th>
                    <th>Material</th>
                    <th>Supplier</th>
                    <th>Supplier batch</th>
                    <th>Allergens</th>
                    <th>Packs</th>
                    <th className="text-right">Qty / Remain</th>
                    <th className="text-right">Expiry</th>
                    <th>By</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((r) => (
                    <tr key={r.id} className={`border-t border-border h-16 ${picked.includes(r.id) ? "bg-rose-50/60 dark:bg-rose-950/20" : ""}`}>
                      {!showArchived && (
                        <td>
                          <Checkbox
                            checked={picked.includes(r.id)}
                            onCheckedChange={() => togglePick(r.id)}
                            data-testid={`intake-select-${r.id}`}
                          />
                        </td>
                      )}
                      <td className="font-mono text-sm">{r.internal_batch_code}</td>
                      <td className="font-medium">{r.material_name}</td>
                      <td className="text-stone-600">{r.supplier_name}</td>
                      <td className="font-mono text-sm">{r.supplier_batch}</td>
                      <td><AllergenTags items={r.allergens} /></td>
                      <td className="text-sm text-stone-600 tnum">
                        {r.pack_count ? (
                          <>
                            {r.pack_count} {r.pack_type}{Number(r.pack_count) !== 1 ? "s" : ""}{" "}
                            × {Number(r.pack_size || 0).toFixed(2)} {r.pack_unit}
                          </>
                        ) : (
                          <span className="text-stone-400">—</span>
                        )}
                      </td>
                      <td className="text-right tnum">
                        {Number(r.quantity || 0).toFixed(2)} /{" "}
                        <span className="font-semibold">
                          {Number(r.remaining || 0).toFixed(2)}
                        </span>{" "}
                        {r.unit}
                      </td>
                      <td className="text-right tnum">
                        {r.expiry_date?.slice(0, 10)}
                      </td>
                      <td>
                        <StaffInitials
                          name={r.received_by_name}
                          testId={`intake-by-${r.id}`}
                        />
                      </td>
                      <td className="flex gap-1 justify-end items-center h-16">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setLabelFor(r)}
                          data-testid={`print-intake-${r.id}`}
                          title="Print internal batch label"
                        >
                          <Printer className="h-4 w-4" />
                        </Button>
                        {showArchived && (
                          <Button size="icon" variant="ghost" onClick={() => setRestoreFor(r)} data-testid={`restore-intake-${r.id}`} title="Restore to live stock">
                            <RotateCcw className="h-4 w-4 text-emerald-700" />
                          </Button>
                        )}
                        {!showArchived && (
                          <>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => startEdit(r)}
                              data-testid={`edit-intake-${r.id}`}
                              title="Edit intake"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => remove(r.id)}
                              data-testid={`delete-intake-${r.id}`}
                              title="Move to archive"
                            >
                              <Archive className="h-4 w-4 text-amber-600" />
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ---- Card view (phones + portrait tablets, below md) ----------- */}
            {!showArchived && (
              <label className="md:hidden flex items-center gap-2 mb-3 text-sm font-medium">
                <Checkbox
                  className="h-5 w-5"
                  checked={allPicked}
                  onCheckedChange={(v) => setPicked(v ? rows.map((r) => r.id) : [])}
                  data-testid="intake-card-select-all"
                />
                Select all
              </label>
            )}
            <div className="md:hidden space-y-3" data-testid="intake-cards">
              {visibleRows.map((r) => (
                <div
                  key={r.id}
                  className="border border-border rounded-lg p-3 bg-white dark:bg-stone-900"
                  data-testid={`intake-card-${r.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    {!showArchived && (
                      <Checkbox
                        className="mt-1 h-5 w-5"
                        checked={picked.includes(r.id)}
                        onCheckedChange={() => togglePick(r.id)}
                        data-testid={`intake-card-select-${r.id}`}
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-base leading-snug truncate">
                        {r.material_name}
                      </div>
                      <div className="text-xs text-stone-500 font-mono mt-0.5 truncate">
                        {r.internal_batch_code}
                      </div>
                    </div>
                    <StaffInitials
                      name={r.received_by_name}
                      testId={`intake-by-${r.id}`}
                    />
                  </div>

                  <div className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-stone-500">Supplier</div>
                      <div className="text-stone-700 dark:text-stone-200 truncate">
                        {r.supplier_name}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-stone-500">Supplier batch</div>
                      <div className="font-mono text-stone-700 dark:text-stone-200 truncate">
                        {r.supplier_batch}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-[10px] uppercase tracking-wider text-stone-500">Qty / Remain</div>
                      <div className="tnum text-base">
                        <span className="text-stone-600">
                          {Number(r.quantity || 0).toFixed(2)}
                        </span>{" "}
                        /{" "}
                        <span className="font-semibold">
                          {Number(r.remaining || 0).toFixed(2)}
                        </span>{" "}
                        {r.unit}
                      </div>
                    </div>
                    {r.pack_count ? (
                      <div className="col-span-2">
                        <div className="text-[10px] uppercase tracking-wider text-stone-500">Packs</div>
                        <div className="tnum text-sm text-stone-700 dark:text-stone-200">
                          {r.pack_count} {r.pack_type}
                          {Number(r.pack_count) !== 1 ? "s" : ""} ×{" "}
                          {Number(r.pack_size || 0).toFixed(2)} {r.pack_unit}
                        </div>
                      </div>
                    ) : null}
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-stone-500">Expiry</div>
                      <div className="tnum text-stone-700 dark:text-stone-200">
                        {r.expiry_date?.slice(0, 10) || "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-stone-500">Allergens</div>
                      <div>
                        {r.allergens?.length ? (
                          <AllergenTags items={r.allergens} />
                        ) : (
                          <span className="text-stone-400 text-xs">—</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 flex gap-2 pt-2 border-t border-border">
                    <Button
                      variant="outline"
                      className="flex-1 h-11"
                      onClick={() => setLabelFor(r)}
                      data-testid={`print-intake-${r.id}`}
                    >
                      <Printer className="h-4 w-4 mr-1.5" /> Label
                    </Button>
                    {showArchived && (
                      <Button variant="outline" className="h-11 px-3 gap-1.5 text-emerald-700" onClick={() => setRestoreFor(r)} data-testid={`restore-intake-card-${r.id}`}>
                        <RotateCcw className="h-4 w-4" /> Restore
                      </Button>
                    )}
                    {!showArchived && <Button
                      variant="outline"
                      className="h-11 px-3 text-amber-600 hover:bg-amber-50"
                      onClick={() => remove(r.id)}
                      data-testid={`delete-intake-${r.id}`}
                      title="Move to archive"
                    >
                      <Archive className="h-4 w-4" />
                    </Button>}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Panel>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="intake-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Record goods intake</DialogTitle>
          </DialogHeader>
          {/*
            Wrapped in a plain <div>, not <form>, so a wedge scanner's
            trailing Enter on the supplier-batch input can't trigger an
            implicit form submission. Some Android Chrome builds were
            interpreting the suppressed-but-still-fired Enter as a
            navigation event and bouncing the user to Chrome's omnibox
            (i.e. a Google search of the scanned code). The Save button
            now calls `submit` explicitly via onClick.
          */}
          <div className="grid grid-cols-2 gap-5" data-testid="intake-form-container">
            <Field label="Supplier">
              <Select
                value={form.supplier_id}
                onValueChange={(v) => setForm({ ...form, supplier_id: v })}
              >
                <SelectTrigger className="h-12" data-testid="intake-supplier">
                  <SelectValue placeholder="Select supplier" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Raw material">
              <Select
                value={form.material_id}
                onValueChange={(v) => {
                  const mat = materials.find((m) => m.id === v);
                  setForm((f) => ({
                    ...f,
                    material_id: v,
                    // Prefill from the material's saved default packaging if
                    // the receiver hasn't typed anything yet (so we never clobber
                    // their in-progress entry).
                    pack_count:
                      f.pack_count === "" && mat?.default_pack_count != null
                        ? String(mat.default_pack_count)
                        : f.pack_count,
                    pack_type:
                      mat?.default_pack_type && f.pack_type === "bag"
                        ? mat.default_pack_type
                        : f.pack_type,
                    pack_size:
                      f.pack_size === "" && mat?.default_pack_size != null
                        ? String(mat.default_pack_size)
                        : f.pack_size,
                    pack_unit:
                      mat?.default_pack_unit || mat?.unit || f.pack_unit,
                  }));
                }}
              >
                <SelectTrigger className="h-12" data-testid="intake-material">
                  <SelectValue placeholder="Select material" />
                </SelectTrigger>
                <SelectContent>
                  {materials.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name} ({m.unit})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Supplier batch number">
              <div className="flex gap-2">
                <Input
                  className="h-12"
                  value={form.supplier_batch}
                  onChange={(e) =>
                    setForm({ ...form, supplier_batch: e.target.value })
                  }
                  data-testid="intake-supplier-batch"
                  placeholder="Scan or type"
                />
                <BarcodeScanner
                  onScan={(v) => setForm((f) => ({ ...f, supplier_batch: v }))}
                  label="Scan"
                />
              </div>
            </Field>
            <Field label="Expiry date (DD/MM/YYYY)">
              <DateInput
                className="h-12"
                value={form.expiry_date}
                onChange={(v) => setForm({ ...form, expiry_date: v })}
                dataTestid="intake-expiry"
                allowPast={false}
              />
            </Field>
            <div className="col-span-2">
              <Field label="Quantity received">
                <div className="grid grid-cols-12 gap-2 items-center">
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    className="col-span-3 h-12"
                    value={form.pack_count}
                    onChange={(e) => setForm({ ...form, pack_count: e.target.value })}
                    placeholder="e.g. 10"
                    data-testid="intake-pack-count"
                  />
                  <Select
                    value={form.pack_type}
                    onValueChange={(v) => setForm({ ...form, pack_type: v })}
                  >
                    <SelectTrigger className="col-span-3 h-12" data-testid="intake-pack-type">
                      <SelectValue placeholder="bags" />
                    </SelectTrigger>
                    <SelectContent>
                      {PACK_TYPES.map((p) => (
                        <SelectItem key={p} value={p}>
                          {p}{p === "unit" ? "" : "s"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="col-span-1 text-center text-stone-500 text-sm">×</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    className="col-span-3 h-12"
                    value={form.pack_size}
                    onChange={(e) => setForm({ ...form, pack_size: e.target.value })}
                    placeholder="e.g. 16"
                    data-testid="intake-pack-size"
                  />
                  <Select
                    value={form.pack_unit}
                    onValueChange={(v) => setForm({ ...form, pack_unit: v })}
                  >
                    <SelectTrigger className="col-span-2 h-12" data-testid="intake-pack-unit">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {["kg", "g", "l", "ml", "unit"].map((u) => (
                        <SelectItem key={u} value={u}>{u}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div
                  className="mt-2 text-xs text-stone-600 flex items-center gap-2"
                  data-testid="intake-total-preview"
                >
                  <span className="uppercase tracking-wider text-[10px] font-bold">Total</span>
                  <span className="font-display font-bold text-base text-primary tnum">
                    {(() => {
                      const t = Number(form.pack_count) * Number(form.pack_size);
                      return Number.isFinite(t) && t > 0 ? `${t} ${form.pack_unit}` : "—";
                    })()}
                  </span>
                  {form.pack_count && form.pack_size && (
                    <span className="text-stone-500">
                      ({form.pack_count} {form.pack_type}{Number(form.pack_count) !== 1 ? "s" : ""} × {form.pack_size} {form.pack_unit})
                    </span>
                  )}
                </div>
              </Field>
            </div>
            <Field label="Location">
              <Select
                value={form.location_id || "none"}
                onValueChange={(v) =>
                  setForm({ ...form, location_id: v === "none" ? "" : v })
                }
              >
                <SelectTrigger className="h-12" data-testid="intake-location">
                  <SelectValue placeholder="Default" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Any —</SelectItem>
                  {locations.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <div className="col-span-2">
              <Field label="Notes">
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="intake-notes"
                />
              </Field>
            </div>
            <DialogFooter className="col-span-2">
              <Button
                variant="ghost"
                type="button"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button type="button" onClick={() => submit()} data-testid="intake-submit" className="h-12 px-6">
                Save intake
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <SupervisorPinModal
        open={!!restoreFor}
        onClose={() => setRestoreFor(null)}
        onVerified={restore}
        roles={["admin"]}
        title="Restore from archive"
        description={restoreFor ? `Enter an admin PIN to restore ${restoreFor.internal_batch_code} (${restoreFor.material_name}) with ${Number(restoreFor.remaining_before_archive || 0).toFixed(2)} ${restoreFor.unit} back to live stock.` : ""}
      />

      <IntakeLabelDialog
        labelFor={labelFor}
        onClose={() => setLabelFor(null)}
        btReady={btReady}
        printer={printer}
      />
    </div>
  );
}
